import React, { FC, useState } from 'react';
import clsx from 'clsx';
import { ButtonLoading } from '../ButtonLoading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faInfinity, faPen, faXmark } from '@fortawesome/free-solid-svg-icons';
import { PROVIDER_DETAILS_FORM_FIELDS } from '../../constants/provider/provider-details-form';
import { Title } from '../developers/page-components/Title';
import { getFormattedNumberValue, VALUE_TYPE } from './NumberFormatterComponent';
import { WhisperComponent } from '../developers/page-components/WhisperComponent';
import { IFormattedProviderProps } from '../../store/tables/admin/provider-data';

interface Props {
  formik: any;
  fieldName: PROVIDER_DETAILS_FORM_FIELDS,
  inputType: 'string' | 'number';
  label: string;
  tooltipText: string;
  placeholder?: string;
  additionalLabelClasses?: string;
  additionalInputClasses?: string;
  additionalContainerClasses?: string;
  loading?: boolean;
  onConfirm: (fieldName: string) => Promise<void>;
  isEditable?: boolean;
  isUnlimited?: boolean;
  metricValue?: number;
  metricType?: VALUE_TYPE;
  metricCurrency?: string;
  formattedValue?: () => string;
  formattedTooltipValue?: () => string;
  showValueInTooltip?: boolean;
  orientation?: 'horizontal' | 'vertical';
  providerDetailsData: IFormattedProviderProps;
}

export const AdvancedInput: FC<Props> = (props) => {
  const {
    formik,
    fieldName,
    inputType,
    label,
    tooltipText,
    placeholder,
    additionalLabelClasses,
    additionalInputClasses,
    additionalContainerClasses,
    loading,
    onConfirm,
    isUnlimited,
    isEditable = false,
    metricValue,
    metricType,
    metricCurrency,
    formattedValue,
    formattedTooltipValue,
    showValueInTooltip,
    orientation = 'vertical',
    providerDetailsData,
  } = props;

  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const handleClose = (): void => {
    if (metricType === 'amount') {
      formik.setFieldValue(fieldName, providerDetailsData?.[fieldName] ? providerDetailsData[fieldName] / 100 : providerDetailsData?.[fieldName]);
    } else {
      formik.setFieldValue(fieldName, providerDetailsData?.[fieldName]);
    }

    setIsEditMode(false);
  };

  const handleOpen = (): void => setIsEditMode(true);

  const handleConfirm = async (): Promise<void> => {
    try {
      await formik.validateField(fieldName);

      if (formik.errors[fieldName]) return;

      await onConfirm(fieldName);

      setIsEditMode(false);
    } catch (error) {
      console.log('handleConfirm error', error);
    }
  };

  const input = (
    <div className={clsx('position-relative', additionalInputClasses)}>
      <div className="d-flex">
        <div className="d-flex align-items-center">
          <div className={clsx('provider-input input-container')}>
            <input
              {...formik.getFieldProps(fieldName)}
              type={inputType}
              className={clsx('form-control form-control-sm form-control-solid', inputType === 'number' && 'number-input-zip',)}
              placeholder={placeholder ? placeholder : ''}
              autoFocus
            />
          </div>
          <div
            className={clsx(
              'provider-icon-box',
              loading ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
            onClick={(): Promise<void> => !loading && handleConfirm()}
          >
            {
              loading
                ? <ButtonLoading />
                : <FontAwesomeIcon icon={faCheck} />
            }

          </div>
          <div
            className={clsx('provider-icon-box',
              loading ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
            onClick={(): void => !loading && handleClose()}
          >
            <FontAwesomeIcon icon={faXmark} />
          </div>

        </div>
      </div>
      {formik.touched[fieldName] && formik.errors[fieldName] && (
        <div className="text-danger fs-9 position-absolute">
          {formik.errors[fieldName]}
        </div>
      )}
    </div>
  );

  const mainText = (
    <div className="d-flex align-items-center">
      {metricCurrency && metricCurrency}
      {getFormattedNumberValue({ initialNumber: metricValue, type: metricType, shorterNumberFormat: true })}
      <span>/</span>
    </div>
  );

  const whisperText = (
    <div className="d-flex align-items-center">
      {metricCurrency && metricCurrency}
      {getFormattedNumberValue({ initialNumber: metricValue, type: metricType })}
    </div>
  );

  return (
    <div className={clsx('mb-5',
      orientation === 'horizontal' && 'd-flex align-items-center',
      additionalContainerClasses
    )}>
      <Title
        title={label}
        tooltipText={tooltipText}
        additionalClass={clsx(
          additionalLabelClasses ? additionalLabelClasses : 'text-muted fs-7',
        )}
      />
      <div className={clsx(
        metricType && 'd-flex',
        orientation === 'horizontal' && 'ms-5',
      )}>
        {
          metricValue && metricValue > 999
            ? <WhisperComponent
                isTooltip
                whisperText={whisperText}
                mainText={mainText}
                additionalClass="cursor-help"
            />
            : (
              <div className="d-flex align-items-center">
                {metricCurrency && <span>{metricCurrency}</span>}
                {getFormattedNumberValue({ initialNumber: metricValue, type: metricType, shorterNumberFormat: true })}
                <span>/</span>
              </div>
            )
        }
        {
          isEditMode
            ? input
            : <FakeTableInput
                onFieldClick={handleOpen}
                isEditable={isEditable}
                value={formik.values[fieldName]}
                isUnlimited={isUnlimited}
                formattedValue={formattedValue}
                formattedTooltipValue={formattedTooltipValue}
                showValueInTooltip={showValueInTooltip}
              />
        }
      </div>
    </div>
  );
};

interface FakeTableInputProps {
  onFieldClick: () => void;
  isEditable?: boolean;
  value: string;
  isUnlimited?: boolean;
  formattedValue?: () => string;
  formattedTooltipValue?: () => string;
  showValueInTooltip?: boolean;
}

export const FakeTableInput: FC<FakeTableInputProps> = (props) => {
  const {
    onFieldClick,
    isEditable,
    value,
    isUnlimited,
    formattedValue,
    formattedTooltipValue,
    showValueInTooltip,
  } = props;

  const getValue = (): string | JSX.Element => {
    if (isUnlimited && (value === null || value === '')) {
      return <FontAwesomeIcon icon={faInfinity} width={12}/>;
    } else if (value && !!formattedTooltipValue && showValueInTooltip) {
      return (
        <WhisperComponent
          isTooltip
          whisperText={formattedTooltipValue()}
          mainText={formattedValue ? formattedValue() : value}
        />
      );
    } else if (value && formattedValue) {
      return formattedValue();
    } else {
      return value ?? '-';
    }
  };

  return (
    <div
      className={clsx('fs-7 fc-text-overflow', isEditable && 'provider-cell cursor-pointer')}
      onClick={(): void => isEditable && onFieldClick()}
    >
      {
        isEditable && <FontAwesomeIcon icon={faPen} className="me-2 w-10px text-danger" />
      }

      {getValue()}

    </div>
  );
};
